import { useEffect, useMemo, useState } from 'react';
import {
  adminAddFavorite,
  adminDeleteEssay,
  adminDeleteEssayError,
  adminUpdateEssayError,
  fetchAdminExport,
  fetchAdminUserDetail,
  fetchAdminUsers,
} from '../api/client';
import type { AdminUserDetail, AdminUserSummary } from '../api/client';
import { useAppStore } from '../store';
import { ERROR_CATEGORY_LABELS, ERROR_CATEGORY_COLORS } from '../types';
import { exportFavoritesToPdf } from '../utils/favoritesPdf';
import {
  AlertTriangle,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  Loader2,
  PencilLine,
  Save,
  Shield,
  Star,
  Trash2,
  Users,
  X,
} from 'lucide-react';

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const parseEssayDbId = (essayId: string) => {
  const match = /^db-(\d+)$/.exec(essayId);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseErrorDbId = (errorId: string) => {
  const match = /^dberr-\d+-(\d+)$/.exec(errorId);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function AdminPage() {
  const { user, setActiveTab } = useAppStore();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [totals, setTotals] = useState({ users: 0, essays: 0, favorites: 0 });
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [expandedEssayIds, setExpandedEssayIds] = useState<string[]>([]);
  const [editingErrorKey, setEditingErrorKey] = useState<string | null>(null);
  const [draftCorrected, setDraftCorrected] = useState('');
  const [draftExplanation, setDraftExplanation] = useState('');
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [isExportingFavorites, setIsExportingFavorites] = useState(false);
  const [savingErrorKey, setSavingErrorKey] = useState<string | null>(null);
  const [deletingEssayId, setDeletingEssayId] = useState<string | null>(null);
  const [deletingErrorKey, setDeletingErrorKey] = useState<string | null>(null);
  const [addingFavoriteErrorKey, setAddingFavoriteErrorKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSelectedUserDetail = async (userId: string) => {
    setIsLoadingDetail(true);
    try {
      const { data } = await fetchAdminUserDetail(userId);
      setDetail(data);
      setExpandedEssayIds((current) => current.filter((id) => data.essays.some((essay) => essay.id === id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载用户详情失败');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') return;

    setIsLoadingList(true);
    setError(null);
    fetchAdminUsers()
      .then(({ data }) => {
        setUsers(data.users);
        setTotals(data.totals);
        const preferredUser =
          data.users.find((item) => item.role !== 'admin' && (item.essayCount > 0 || item.favoriteCount > 0)) ||
          data.users.find((item) => item.role !== 'admin') ||
          data.users[0] ||
          null;
        setSelectedUserId((current) => current || preferredUser?.id || null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '加载管理数据失败');
      })
      .finally(() => {
        setIsLoadingList(false);
      });
  }, [user]);

  useEffect(() => {
    if (!selectedUserId || !user || user.role !== 'admin') return;
    setError(null);
    void loadSelectedUserDetail(selectedUserId);
  }, [selectedUserId, user]);

  const selectedSummary = useMemo(
    () => users.find((item) => item.id === selectedUserId) || null,
    [selectedUserId, users]
  );

  const favoriteErrorIds = useMemo(
    () => new Set((detail?.favorites || []).map((favorite) => favorite.error.id)),
    [detail]
  );

  const toggleEssayExpanded = (essayId: string) => {
    setExpandedEssayIds((current) =>
      current.includes(essayId) ? current.filter((id) => id !== essayId) : [...current, essayId]
    );
  };

  const startEditing = (essayId: string, errorId: string, corrected: string, explanation: string) => {
    setEditingErrorKey(`${essayId}:${errorId}`);
    setDraftCorrected(corrected);
    setDraftExplanation(explanation);
    setError(null);
  };

  const cancelEditing = () => {
    setEditingErrorKey(null);
    setDraftCorrected('');
    setDraftExplanation('');
  };

  const handleSaveError = async (essayId: string, errorId: string) => {
    if (!selectedUserId || !draftCorrected.trim()) return;

    const backendEssayId = parseEssayDbId(essayId);
    const backendErrorId = parseErrorDbId(errorId);
    if (!backendEssayId || !backendErrorId) {
      setError('无法解析要编辑的分析结果 ID');
      return;
    }

    const actionKey = `${essayId}:${errorId}`;
    setSavingErrorKey(actionKey);
    setError(null);
    try {
      await adminUpdateEssayError(selectedUserId, backendEssayId, backendErrorId, {
        correctedSentence: draftCorrected.trim(),
        explanationZh: draftExplanation.trim(),
      });
      await loadSelectedUserDetail(selectedUserId);
      cancelEditing();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存分析结果失败');
    } finally {
      setSavingErrorKey(null);
    }
  };

  const handleDeleteEssay = async (essayId: string, essayTitle: string) => {
    if (!selectedUserId) return;
    if (!window.confirm(`确认删除《${essayTitle}》吗？该作文及其分析结果会被永久删除。`)) return;

    const backendEssayId = parseEssayDbId(essayId);
    if (!backendEssayId) {
      setError('无法解析作文 ID');
      return;
    }

    setDeletingEssayId(essayId);
    setError(null);
    try {
      await adminDeleteEssay(selectedUserId, backendEssayId);
      await loadSelectedUserDetail(selectedUserId);
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除作文失败');
    } finally {
      setDeletingEssayId(null);
    }
  };

  const handleDeleteError = async (essayId: string, essayTitle: string, errorId: string) => {
    if (!selectedUserId) return;
    if (!window.confirm(`确认删除《${essayTitle}》中的这条分析结果吗？`)) return;

    const backendEssayId = parseEssayDbId(essayId);
    const backendErrorId = parseErrorDbId(errorId);
    if (!backendEssayId || !backendErrorId) {
      setError('无法解析分析结果 ID');
      return;
    }

    const actionKey = `${essayId}:${errorId}`;
    setDeletingErrorKey(actionKey);
    setError(null);
    try {
      await adminDeleteEssayError(selectedUserId, backendEssayId, backendErrorId);
      await loadSelectedUserDetail(selectedUserId);
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除分析结果失败');
    } finally {
      setDeletingErrorKey(null);
    }
  };

  const handleAddFavorite = async (errorId: string) => {
    if (!selectedUserId) return;

    const backendErrorId = parseErrorDbId(errorId);
    if (!backendErrorId) {
      setError('无法解析分析结果 ID');
      return;
    }

    setAddingFavoriteErrorKey(errorId);
    setError(null);
    try {
      await adminAddFavorite(selectedUserId, backendErrorId);
      await loadSelectedUserDetail(selectedUserId);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加入错题集失败');
    } finally {
      setAddingFavoriteErrorKey(null);
    }
  };

  const handleExportFavoritesPdf = async () => {
    if (!selectedSummary || !detail) return;
    setIsExportingFavorites(true);
    setError(null);
    try {
      const dateStr = new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
      await exportFavoritesToPdf(
        detail.favorites,
        `Refine-${selectedSummary.email.replace(/[^a-z0-9._-]+/gi, '_')}-错题集.pdf`,
        `${selectedSummary.name} 的语法错题集`,
        `共 ${detail.favorites.length} 个错误 · 导出于 ${dateStr}`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : '导出 PDF 失败');
    } finally {
      setIsExportingFavorites(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6">
        <div className="text-center">
          <AlertTriangle size={48} className="text-slate-300 mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-primary mb-2">需要管理员权限</h2>
          <p className="text-slate-500 mb-6">当前账号无法访问管理台。</p>
          <button
            onClick={() => setActiveTab('home')}
            className="px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface py-10 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-primary mb-2 flex items-center gap-3">
              <Shield size={32} className="text-accent" />
              管理台
            </h1>
            <p className="text-slate-500">查看用户作文、调整分析结果、加入错题集并导出。</p>
          </div>
          <button
            onClick={async () => {
              setIsExportingAll(true);
              setError(null);
              try {
                const { data } = await fetchAdminExport();
                downloadJson(data, `refine-admin-export-${new Date().toISOString().slice(0, 10)}.json`);
              } catch (err) {
                setError(err instanceof Error ? err.message : '导出失败');
              } finally {
                setIsExportingAll(false);
              }
            }}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
            disabled={isExportingAll}
          >
            {isExportingAll ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
            {isExportingAll ? '导出中...' : '导出全站 JSON'}
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm">
            <div className="text-xs text-slate-500 mb-1">总用户数</div>
            <div className="font-serif text-3xl text-slate-900">{totals.users}</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm">
            <div className="text-xs text-slate-500 mb-1">总作文数</div>
            <div className="font-serif text-3xl text-emerald-700">{totals.essays}</div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm">
            <div className="text-xs text-slate-500 mb-1">总错题收藏</div>
            <div className="font-serif text-3xl text-amber-600">{totals.favorites}</div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[360px_1fr] gap-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Users size={18} className="text-slate-400" />
              <h2 className="font-serif text-xl text-primary">用户列表</h2>
            </div>

            {isLoadingList ? (
              <div className="py-10 text-center text-slate-400">
                <Loader2 size={20} className="animate-spin mx-auto mb-3" />
                正在加载用户...
              </div>
            ) : (
              <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                {users.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setSelectedUserId(item.id)}
                    className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                      selectedUserId === item.id
                        ? 'border-emerald-200 bg-emerald-50/60'
                        : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{item.name}</div>
                        <div className="text-xs text-slate-500 truncate">{item.email}</div>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${item.role === 'admin' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'}`}>
                        {item.role === 'admin' ? '管理员' : '用户'}
                      </span>
                    </div>
                    <div className="flex gap-3 text-xs text-slate-500">
                      <span>{item.essayCount} 篇作文</span>
                      <span>{item.favoriteCount} 条错题</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm min-h-[520px]">
            {!selectedSummary ? (
              <div className="h-full flex items-center justify-center text-center text-slate-400">
                还没有可查看的用户
              </div>
            ) : isLoadingDetail || !detail ? (
              <div className="h-full flex items-center justify-center text-center text-slate-400">
                <div>
                  <Loader2 size={22} className="animate-spin mx-auto mb-3" />
                  正在加载用户详情...
                </div>
              </div>
            ) : (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
                  <div>
                    <h2 className="font-serif text-2xl text-primary mb-1">{detail.user.name}</h2>
                    <p className="text-sm text-slate-500">{detail.user.email}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {detail.user.role === 'admin' ? '当前查看的是管理员账号' : '当前查看的是普通用户账号'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleExportFavoritesPdf}
                      disabled={isExportingFavorites || detail.favorites.length === 0}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-100 transition-colors disabled:opacity-50"
                    >
                      {isExportingFavorites ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                      导出该用户错题集 PDF
                    </button>
                    <button
                      onClick={() => {
                        downloadJson(
                          {
                            exportedAt: new Date().toISOString(),
                            user: selectedSummary,
                            essays: detail.essays,
                            favorites: detail.favorites,
                          },
                          `refine-user-${detail.user.email.replace(/[^a-z0-9._-]+/gi, '_')}.json`
                        );
                      }}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-100 transition-colors"
                    >
                      <Download size={16} />
                      导出该用户 JSON
                    </button>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
                    <div className="text-xs text-slate-500 mb-1">作文数量</div>
                    <div className="font-serif text-3xl text-slate-900">{detail.essays.length}</div>
                  </div>
                  <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
                    <div className="text-xs text-slate-500 mb-1">错题收藏</div>
                    <div className="font-serif text-3xl text-amber-600">{detail.favorites.length}</div>
                  </div>
                </div>

                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText size={18} className="text-slate-400" />
                    <h3 className="font-serif text-xl text-primary">该用户的作文</h3>
                  </div>
                  <div className="space-y-4">
                    {detail.essays.length === 0 && (
                      <div className="text-sm text-slate-400">该用户还没有上传作文。</div>
                    )}
                    {detail.essays.map((essay) => {
                      const isExpanded = expandedEssayIds.includes(essay.id);
                      return (
                        <div key={essay.id} className="rounded-2xl border border-slate-100 p-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-3">
                            <div>
                              <div className="font-semibold text-slate-900">{essay.title}</div>
                              <div className="text-xs text-slate-500">
                                {new Date(essay.uploadedAt).toLocaleString('zh-CN')} · {essay.errors.length} 处分析结果
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => toggleEssayExpanded(essay.id)}
                                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                              >
                                {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                {isExpanded ? '收起全文' : '查看全文'}
                              </button>
                              <button
                                onClick={() => void handleDeleteEssay(essay.id, essay.title)}
                                disabled={deletingEssayId === essay.id}
                                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                              >
                                {deletingEssayId === essay.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                删除作文
                              </button>
                            </div>
                          </div>

                          <p className={`text-sm text-slate-600 leading-relaxed whitespace-pre-wrap ${isExpanded ? '' : 'line-clamp-4'}`}>
                            {essay.content}
                          </p>

                          <div className="mt-4 space-y-3">
                            {essay.errors.length === 0 && (
                              <div className="text-sm text-slate-400">这篇作文还没有分析结果。</div>
                            )}
                            {essay.errors.map((errorItem) => {
                              const actionKey = `${essay.id}:${errorItem.id}`;
                              const isEditing = editingErrorKey === actionKey;
                              const isFavorited = favoriteErrorIds.has(errorItem.id);
                              return (
                                <div key={errorItem.id} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                                  <div className="flex items-start justify-between gap-4 mb-3">
                                    <span
                                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold text-white"
                                      style={{ backgroundColor: ERROR_CATEGORY_COLORS[errorItem.category] }}
                                    >
                                      {ERROR_CATEGORY_LABELS[errorItem.category]}
                                    </span>
                                    <div className="flex flex-wrap gap-2">
                                      {isEditing ? (
                                        <>
                                          <button
                                            onClick={cancelEditing}
                                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                                          >
                                            <X size={12} />
                                            取消
                                          </button>
                                          <button
                                            onClick={() => void handleSaveError(essay.id, errorItem.id)}
                                            disabled={!draftCorrected.trim() || savingErrorKey === actionKey}
                                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-accent text-white hover:bg-emerald-700 disabled:opacity-50"
                                          >
                                            {savingErrorKey === actionKey ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                                            保存
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          onClick={() => startEditing(essay.id, errorItem.id, errorItem.correctedSentence, errorItem.explanationZh || '')}
                                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                                        >
                                          <PencilLine size={12} />
                                          修改分析
                                        </button>
                                      )}
                                      <button
                                        onClick={() => void handleAddFavorite(errorItem.id)}
                                        disabled={isFavorited || addingFavoriteErrorKey === errorItem.id}
                                        className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors disabled:opacity-50 ${isFavorited ? 'border-amber-200 bg-amber-50 text-amber-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                                      >
                                        {addingFavoriteErrorKey === errorItem.id ? <Loader2 size={12} className="animate-spin" /> : <Star size={12} />}
                                        {isFavorited ? '已在错题集' : '加入错题集'}
                                      </button>
                                      <button
                                        onClick={() => void handleDeleteError(essay.id, essay.title, errorItem.id)}
                                        disabled={deletingErrorKey === actionKey}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                                      >
                                        {deletingErrorKey === actionKey ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                        删除分析
                                      </button>
                                    </div>
                                  </div>

                                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                                    <div className="rounded-xl bg-red-50 border border-red-100 p-3">
                                      <div className="text-[11px] font-bold text-red-400 mb-1">原句</div>
                                      <p className="text-sm text-red-800 leading-relaxed">{errorItem.originalSentence}</p>
                                    </div>
                                    <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                                      <div className="text-[11px] font-bold text-emerald-400 mb-1">修改后</div>
                                      {isEditing ? (
                                        <textarea
                                          value={draftCorrected}
                                          onChange={(event) => setDraftCorrected(event.target.value)}
                                          rows={3}
                                          className="w-full text-sm text-emerald-900 leading-relaxed bg-white/90 border border-emerald-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-300"
                                        />
                                      ) : (
                                        <p className="text-sm text-emerald-800 leading-relaxed">{errorItem.correctedSentence}</p>
                                      )}
                                    </div>
                                  </div>

                                  <div className="rounded-xl bg-white border border-slate-100 p-3">
                                    <div className="text-[11px] font-bold text-slate-400 mb-1">中文解析</div>
                                    {isEditing ? (
                                      <textarea
                                        value={draftExplanation}
                                        onChange={(event) => setDraftExplanation(event.target.value)}
                                        rows={4}
                                        className="w-full text-sm text-slate-700 leading-relaxed bg-white border border-slate-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-slate-300"
                                      />
                                    ) : (
                                      <p className="text-sm text-slate-600 leading-relaxed">{errorItem.explanationZh || '暂无中文解析'}</p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <BookOpen size={18} className="text-slate-400" />
                    <h3 className="font-serif text-xl text-primary">该用户的错题集</h3>
                  </div>
                  <div className="space-y-3">
                    {detail.favorites.length === 0 && (
                      <div className="text-sm text-slate-400">该用户还没有收藏错题。</div>
                    )}
                    {detail.favorites.map((favorite) => (
                      <div key={favorite.error.id} className="rounded-2xl border border-slate-100 p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <span
                            className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold text-white"
                            style={{ backgroundColor: ERROR_CATEGORY_COLORS[favorite.error.category] }}
                          >
                            {ERROR_CATEGORY_LABELS[favorite.error.category]}
                          </span>
                          <span className="text-xs text-slate-400">{favorite.error.essayTitle}</span>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                          <div className="rounded-xl bg-red-50 border border-red-100 p-3">
                            <div className="text-[11px] font-bold text-red-400 mb-1">原句</div>
                            <p className="text-sm text-red-800 leading-relaxed">{favorite.error.originalSentence}</p>
                          </div>
                          <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                            <div className="text-[11px] font-bold text-emerald-400 mb-1">修改后</div>
                            <p className="text-sm text-emerald-800 leading-relaxed">{favorite.error.correctedSentence}</p>
                          </div>
                        </div>
                        <p className="mt-3 text-sm text-slate-500 leading-relaxed">{favorite.error.explanationZh || '暂无中文解析'}</p>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
